/**
 * @fileoverview Route guard that requires an active organization selection.
 * @module features/orgs/components/RequireOrg
 */

import { ReactElement, useEffect, useRef } from 'react'
import { useActiveOrg } from '../context/ActiveOrgProvider'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * Guard component that blocks rendering until an organization is selected.
 * Defers navigation to a side-effect to avoid React Router warnings and race conditions.
 *
 * @component
 */
export function RequireOrg({ children }: { children: ReactElement }): ReactElement {
  const navigate = useNavigate()
  const location = useLocation()
  const { activeOrgId, orgs, ready } = useActiveOrg()
  const redirectedRef = useRef(false)

  useEffect(() => {
    if (!ready || redirectedRef.current) return
    // If user has zero orgs after bootstrap, go to onboarding
    if (!activeOrgId && orgs.length === 0) {
      redirectedRef.current = true
      navigate('/onboarding', { replace: true })
      return
    }
    // If user has orgs and is currently on onboarding, send to dashboard
    if (activeOrgId && location.pathname.startsWith('/onboarding')) {
      redirectedRef.current = true
      navigate('/dashboard', { replace: true })
    }
  }, [ready, activeOrgId, orgs.length, navigate, location.pathname])

  if (!ready) {
    return (
      <div className="flex min-h-svh w-full items-center justify-center p-6">
        <div className="text-sm text-muted-foreground">Loading your workspace…</div>
      </div>
    )
  }

  if (!activeOrgId) {
    return (
      <div className="flex min-h-svh w-full items-center justify-center p-6">
        <div className="text-sm text-muted-foreground">Select an organization from the menu.</div>
      </div>
    )
  }
  return children
}
